import { useEffect, useState } from 'react'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Textarea } from '@jira-clone/ui'
import {
  CATEGORY_LABEL,
  MANUAL_TIMESHEET_CATEGORIES,
  validateTimesheetRow,
  type DepartmentTimesheetConfig,
  type TimesheetCategory,
} from '@jira-clone/shared-types'
import { durationInputValue, parseDuration, shortDay, weekdayName } from '../../../lib/timesheet'
import { useProjectTasks, type RowFormValues, type TimesheetOptions, type TimesheetRowDto } from '../api'

const SELECT = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring'

interface EntryDialogProps {
  open: boolean
  onClose: () => void
  /** The day a new entry starts on. */
  date: string
  /** The seven days of the week, so the entry can be moved to another day. */
  weekDates: string[]
  config: DepartmentTimesheetConfig
  channels: string[]
  options: TimesheetOptions | undefined
  /** An entry being edited; null adds a new one. */
  initial: TimesheetRowDto | null
  saving: boolean
  /** The server's answer when it refused the entry. */
  serverError: string | null
  onSave: (values: RowFormValues) => void
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function EntryDialog({ open, onClose, date, weekDates, config, channels, options, initial, saving, serverError, onSave }: EntryDialogProps) {
  const [form, setForm] = useState({
    date,
    category: 'work' as TimesheetCategory,
    activityType: '',
    projectId: '',
    taskId: '',
    leadId: '',
    dealId: '',
    campaign: '',
    channel: '',
    deliverable: '',
    revisionRound: '',
    filingPeriod: '',
    duration: '',
    note: '',
  })
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setProblem(null)
    setForm({
      date: initial?.date ?? date,
      category: initial?.category ?? 'work',
      activityType: initial?.activityType ?? '',
      projectId: initial?.projectId ?? '',
      taskId: initial?.taskId ?? '',
      leadId: initial?.leadId ?? '',
      dealId: initial?.dealId ?? '',
      campaign: initial?.campaign ?? '',
      channel: initial?.channel ?? '',
      deliverable: initial?.deliverable ?? '',
      revisionRound: initial?.revisionRound != null ? String(initial.revisionRound) : '',
      filingPeriod: initial?.filingPeriod ?? '',
      duration: durationInputValue(initial?.durationMinutes),
      note: initial?.note ?? '',
    })
  }, [open, initial, date])

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }))

  const links = new Set([...config.requiredLinks, ...config.oneOfLinks, ...config.optionalLinks])
  const isWork = form.category === 'work'
  const tasks = useProjectTasks(isWork && links.has('task') ? form.projectId || null : null)

  const submit = () => {
    const minutes = parseDuration(form.duration)
    if (minutes === null) {
      setProblem('Enter the time spent, like 1.5, 1:30 or 90m.')
      return
    }
    const values: RowFormValues = {
      date: form.date,
      durationMinutes: minutes,
      category: form.category,
      note: form.note.trim() || null,
    }
    if (isWork) {
      values.activityType = form.activityType || null
      values.projectId = form.projectId || null
      values.taskId = form.taskId || null
      values.leadId = form.leadId || null
      values.dealId = form.dealId || null
      values.campaign = form.campaign.trim() || null
      values.channel = form.channel || null
      values.deliverable = form.deliverable.trim() || null
      values.revisionRound = form.revisionRound === '' ? null : Number(form.revisionRound)
      values.filingPeriod = form.filingPeriod.trim() || null
    }
    const errors = validateTimesheetRow(config.department, values)
    if (errors.length > 0) {
      setProblem(errors[0])
      return
    }
    setProblem(null)
    onSave(values)
  }

  const needs = (name: 'project' | 'lead' | 'deal' | 'campaign' | 'task') => links.has(name)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit entry' : 'Add entry'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3.5 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Day">
              <select className={SELECT} value={form.date} onChange={(e) => set('date', e.target.value)} aria-label="Day">
                {weekDates.map((d) => (
                  <option key={d} value={d}>
                    {weekdayName(d)} {shortDay(d)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Time spent" hint="1.5, 1:30 or 90m">
              <Input value={form.duration} onChange={(e) => set('duration', e.target.value)} placeholder="1:30" aria-label="Time spent" autoFocus />
            </Field>
          </div>

          <Field label="Type">
            <select className={SELECT} value={form.category} onChange={(e) => set('category', e.target.value as TimesheetCategory)} aria-label="Type">
              {MANUAL_TIMESHEET_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c === 'work' ? `${config.department} work` : CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </Field>

          {isWork && (
            <>
              <Field label="Activity">
                <select className={SELECT} value={form.activityType} onChange={(e) => set('activityType', e.target.value)} aria-label="Activity">
                  <option value="">Choose…</option>
                  {config.activities.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </Field>

              {needs('project') && (
                <Field label={links.has('campaign') && !config.requiredLinks.includes('project') ? 'Project (or a campaign)' : 'Project'}>
                  <select className={SELECT} value={form.projectId} onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value, taskId: '' }))} aria-label="Project">
                    <option value="">{config.requiredLinks.includes('project') ? 'Choose…' : 'None'}</option>
                    {(options?.projects ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {needs('task') && form.projectId && (
                <Field label="Task (optional)">
                  <select className={SELECT} value={form.taskId} onChange={(e) => set('taskId', e.target.value)} aria-label="Task">
                    <option value="">None</option>
                    {(tasks.data?.tasks ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {needs('campaign') && (
                <Field label="Campaign">
                  <Input value={form.campaign} onChange={(e) => set('campaign', e.target.value)} placeholder="e.g. Diwali offer" aria-label="Campaign" />
                </Field>
              )}
              {needs('lead') && (
                <Field label={config.oneOfLinks.length > 0 ? 'Lead (or a deal)' : 'Lead'}>
                  <select className={SELECT} value={form.leadId} onChange={(e) => set('leadId', e.target.value)} aria-label="Lead">
                    <option value="">None</option>
                    {(options?.leads ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {needs('deal') && (
                <Field label={config.oneOfLinks.length > 0 ? 'Deal (or a lead)' : 'Deal'}>
                  <select className={SELECT} value={form.dealId} onChange={(e) => set('dealId', e.target.value)} aria-label="Deal">
                    <option value="">None</option>
                    {(options?.deals ?? []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              {config.extraFields.includes('channel') && (
                <Field label="Channel (optional)">
                  <select className={SELECT} value={form.channel} onChange={(e) => set('channel', e.target.value)} aria-label="Channel">
                    <option value="">None</option>
                    {channels.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {config.extraFields.includes('deliverable') && (
                <Field label="Deliverable (optional)">
                  <Input value={form.deliverable} onChange={(e) => set('deliverable', e.target.value)} placeholder="e.g. Homepage banner" aria-label="Deliverable" />
                </Field>
              )}
              {config.extraFields.includes('revisionRound') && (
                <Field label="Revision round (optional)">
                  <Input type="number" min={0} max={99} value={form.revisionRound} onChange={(e) => set('revisionRound', e.target.value)} aria-label="Revision round" />
                </Field>
              )}
              {config.extraFields.includes('filingPeriod') && (
                <Field label="Filing period (optional)" hint="Year and month, like 2026-10">
                  <Input value={form.filingPeriod} onChange={(e) => set('filingPeriod', e.target.value)} placeholder="2026-10" aria-label="Filing period" />
                </Field>
              )}
            </>
          )}

          <Field label={isWork ? 'Note (optional)' : 'Note'}>
            <Textarea value={form.note} onChange={(e) => set('note', e.target.value)} rows={2} maxLength={500} placeholder="What was it?" aria-label="Note" />
          </Field>

          {(problem || serverError) && (
            <p role="alert" className="text-sm text-destructive">
              {problem || serverError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : initial ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
